import { useCallback, useEffect, useRef, useState } from 'react';
import type { Article } from '@erp/data-access/interfaces';

const SCAN_MAX_KEY_INTERVAL = 80;
const MIN_BARCODE_LENGTH = 3;
const FEEDBACK_DURATION = 2000;

export interface ScanFeedback {
  type: 'success' | 'error';
  text: string;
}

/**
 * Global barcode scanner detection — no input focus needed.
 *
 * Barcode scanners type characters extremely fast (< 50 ms between keys)
 * then send Enter.  This hook intercepts those keystrokes at the window
 * level in the capture phase and **prevents them from reaching any input**.
 *
 * Flow:
 *  1. First rapid keystroke arrives → start buffering, block it.
 *  2. Subsequent rapid keystrokes → keep buffering, keep blocking.
 *  3. If a keystroke is too slow → dump buffered chars into the focused
 *     input (it was human typing), reset.
 *  4. Enter arrives while buffer is full → look up article, call callback.
 */
export interface UseBarcodeScannerOptions {
  /**
   * When false, the global keydown listener is not attached so that
   * scanner keystrokes flow normally into the focused input. Use this
   * to disable auto-add while a modal (e.g. the article picker) is open.
   * Defaults to true.
   */
  enabled?: boolean;
}

export function useBarcodeScanner(
  articles: Article[] | undefined,
  onArticleScanned: (article: Article) => void,
  options: UseBarcodeScannerOptions = {},
) {
  const { enabled = true } = options;
  const [scanFeedback, setScanFeedback] = useState<ScanFeedback | null>(null);
  const feedbackTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  const bufferRef = useRef('');
  const lastKeyTimeRef = useRef(0);
  const isScanningRef = useRef(false);

  const barcodeMapRef = useRef<Map<string, Article>>(new Map());
  useEffect(() => {
    const map = new Map<string, Article>();
    articles?.forEach((a) => {
      if (a.barcode) map.set(a.barcode.toLowerCase(), a);
      if (a.code) map.set(a.code.toLowerCase(), a);
    });
    barcodeMapRef.current = map;
  }, [articles]);

  const showFeedback = useCallback((type: ScanFeedback['type'], text: string) => {
    clearTimeout(feedbackTimer.current);
    setScanFeedback({ type, text });
    feedbackTimer.current = setTimeout(() => setScanFeedback(null), FEEDBACK_DURATION);
  }, []);

  const onScannedRef = useRef(onArticleScanned);
  onScannedRef.current = onArticleScanned;

  useEffect(() => {
    if (!enabled) {
      // Reset any in-progress scan state when disabled so we don't leak it
      // into the next enable cycle.
      bufferRef.current = '';
      isScanningRef.current = false;
      lastKeyTimeRef.current = 0;
      return;
    }

    function reset() {
      bufferRef.current = '';
      isScanningRef.current = false;
    }

    function handleKeyDown(e: KeyboardEvent) {
      const now = Date.now();
      const elapsed = now - lastKeyTimeRef.current;

      // ── Enter: process buffer if we're in scan mode ───────────
      if (e.key === 'Enter') {
        if (isScanningRef.current && bufferRef.current.length >= MIN_BARCODE_LENGTH) {
          e.preventDefault();
          e.stopImmediatePropagation();

          const barcode = bufferRef.current;
          const article = barcodeMapRef.current.get(barcode.toLowerCase());
          if (article) {
            onScannedRef.current(article);
            showFeedback('success', `${article.name} (+1)`);
          } else {
            showFeedback('error', barcode);
          }
        }
        reset();
        lastKeyTimeRef.current = 0;
        return;
      }

      // Only care about printable single characters
      if (e.key.length !== 1) return;

      // ── Fast keystroke: scanner pattern ────────────────────────
      if (lastKeyTimeRef.current > 0 && elapsed <= SCAN_MAX_KEY_INTERVAL) {
        // We're in scan mode — block keystroke from reaching any input
        isScanningRef.current = true;
        e.preventDefault();
        e.stopImmediatePropagation();
        bufferRef.current += e.key;
        lastKeyTimeRef.current = now;
        return;
      }

      // ── Second character arriving fast after the first ────────
      // The very first char of a scan wasn't blocked (we didn't know yet).
      // If this second char arrives fast, we're now confident it's a scan.
      if (bufferRef.current.length === 1 && elapsed <= SCAN_MAX_KEY_INTERVAL) {
        isScanningRef.current = true;
        e.preventDefault();
        e.stopImmediatePropagation();
        bufferRef.current += e.key;
        lastKeyTimeRef.current = now;
        return;
      }

      // ── Slow keystroke: human typing ──────────────────────────
      // Reset and start fresh — this char might be the first of a new scan
      bufferRef.current = e.key;
      lastKeyTimeRef.current = now;
      isScanningRef.current = false;
      // Don't preventDefault — let it reach the input normally
    }

    // Capture phase + highest priority
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [showFeedback, enabled]);

  return { scanFeedback };
}
